import axios, { AxiosError } from 'axios';
import { API_GATEWAY_BASE_URL } from '@/core/global-data/constants.data';
import router from '@/core/global-router';
import { toast } from 'sonner';
import { clearAuthTokens } from '@/features/auth/guard/utils';

// ------------- AXIOS DEFAULTS SETTINGS -------------
// ---------------------------------------------------
const defaultURL = API_GATEWAY_BASE_URL;

axios.defaults.baseURL = defaultURL;

// ------------- GLOBAL ERROR HANDLER -------------

const handleUnauthorized = () => {
    // 1. Clear authentication tokens
    clearAuthTokens();

    // 2. Redirect to login page
    router.navigate('/login');

    // 3. Optional: Refresh the page to clear application state
    window.location.reload();
};

// ------------- CREATE AXIOS INTERCEPTORS -------------
// ----------------------------------------------------

axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const status = error.response?.status;
        const isUnauthorized = status === 401;

        if (isUnauthorized && !error.config?.url?.includes('/login')) {
            handleUnauthorized();
        }

        if (status === 403 && error.response?.data?.message) {
            toast.error(error.response?.data?.message);
        }

        return Promise.reject(error);
    },
);

// -------------------------------------------------------

export const endpoints = {
    auth: {
        login: '/login',
        profile: '/user',
        me: '/me',
        changePassword: '/change-password',
        forgotPassword: '/forgot-password',
        resetPassword: '/reset-password',
    },
    dashboard: {
        analytics: '/gym-members-dashboard',
    },

    member: {
        create: '/gym-members-store',
        update: '/gym-members-update/',
        delete: '/gym-members-delete/',
        list: '/gym-members-paginated',
        select: '/gym-members-simple',
        show: '/gym-members-show/',
        dashboard: '/gym-members-analytics',
        block: '/gym-members-add-to-black-list/',

        logs: '/logs/user',
        logscleanup: '/logs/cleanup/',
        dashboardType: '/members-analytics-type',
        images: '/member-update-personal-images/',
        license: '/member-update-license-images/',
        dashboardNewmembers: '/member-new-dashboard',
        typeTrack: '/members-type-tracks/',
        typeTracks: '/members-type-tracks',
    },
    packages: {
        list: '/gym-packages-paginated',
        create: '/gym-packages-store',
        dashboard: '/gym-packages-analytics',
        update: '/gym-packages-update',
        delete: '/gym-packages-delete/',
        show: '/gym-packages-show',
        select: '/gym-packages-simple',
    },
    enrollmentsProducts: {
        list: '/gym-member-product-enrollments',
        create: '/gym-member-product-enrollments-enroll',
        // update: '/gym-member-product-enrollments-update-status',
        delete: '/gym-member-product-enrollments-unenroll/',
        updateStatus: '/gym-member-product-enrollments-update/',
        // 
        show: '/gym-member-product-enrollments-show',
        dashboard: '/gym-member-product-enrollments-analytics',
        select:"gym-products-simple"
    },
    enrollmentsPackages: {
        list: '/gym-member-package-enrollments',
        create: '/gym-member-package-enrollments-enroll',
        delete: '/gym-member-package-enrollments-unenroll/',
        updateStatus: '/gym-member-package-enrollments-update-status/',
        show: '/gym-member-package-enrollments-show',
        dashboard: '/gym-member-package-enrollments-analytics',
    },

    allProducts: {
        list: '/gym-products-paginated',
        create: '/gym-products-store',
        update: '/gym-products-update',
        delete: '/gym-products-delete/',
        show: '/gym-products-show',
        dashboard: '/gym-products-analytics',
        // post: '/website-product-service',
        // get: '/website-products',
        // select: '/products-simple',
    },

    // -------------------------------------------------------
    subscriptions: {
        list: '/subscriptions-paginated',
        create: '/subscriptions-store',
        dashboard: '/subscriptions-analytics',
        update: '/subscriptions-update/',
        delete: '/subscriptions-delete/',
        show: '/subscriptions-show/',
        subscriptions: '/member-subscriptions',
    },

    trainers: {
        list: '/gym-trainers-paginated',
        show: '/gym-trainers-show/',
        create: '/gym-trainers-store',
        update: '/gym-trainers-update/',
        delete: '/gym-trainers-delete/',
        dashboard: '/gym-trainers-analytics',
        select: '/gym-trainers-simple',
        packageList: '/users-courses-list/',
        ProductsList: '/users-products-list/',
    },
    //-------------------------------------------------------

    complaint: {
        list: '/member-complaints-paginated',
        show: '/member-complaints-show/',
        update: '/member-complaints-resolve/',
        // update: '/members-complaints/',
        create: '/member-complaints-store',
        delete: '/members-delete/',
        resolve: '/member-complaints-resolve/',
        dashboard: '/member-complaints-statistics',
        open: '/member-complaints-open',
    },

    cutomerContact: {
        list: '/member-emails-paginated',
        create: '/member-emails-store',
        dashboard: '/member-emails-statistics',
        show: '/member-emails-show/', //member-emails-show
        update: '/member-emails-update',
        email: '/members-emails/',
    },

    products: {
        list: '/products-paginated',
        create: '/products-store',
        update: '/products-update',
        delete: '/products-delete/',
        show: '/products-show',
        dashboard: '/products-analytics',
        select: '/products-simple',
    },
    finance: {
        payments: '/orders-payment-analytics',
    },
    notification: {
        list: '/notification-list',
        count: '/notification-count',
        delete: '/notification-delete/',
        read: '/notification-mark-read/',
        allRead: '/notification-mark-all-read',
        mail: '/notification-mail',
        generate: '/notification-generate',
        sendEmail: '/notification-send-emails',
    },

   
    branch: {
        create: '/branches-store',
        list: '/branches-paginated',
        show: '/branches-show/',
        update: '/branches-update/',
        delete: '/branches-delete/',
        dashboard: 'branches-analytics',
        select: 'branches-simple',
    },
    employee: {
        list: '/users-paginated',
        show: '/users-show/',
        create: '/users-store',
        update: '/users-update/',
        delete: '/users-delete/',
        dashboard: '/users-analytics',
        select: '/users-simple',
        attendance: '/users-attendance',
        salary: '/users-salary/',
        salaryList: '/users-salary-list',
        logs: '/user-logs/user/',
        usersOrdersProducts: '/users-orders-and-product-orders/',
    },
    roles: {
        list: '/role-list',
        create: '/role-create',
        update: '/role-update/',
        delete: '/role-delete/',
        show: '/role-permissions/',
    },
    permissions: {
        list: '/permission-list',
        create: '/permission-create',
        update: '/permission-update/',
        delete: '/permission-delete/',
    },
    userLimits: {
        list: '/user-limits',
    },
    orders: {
        create: '/orders-store',
        update: '/orders-update',
        delete: '/orders-delete/',
        list: '/orders-paginated',
        show: '/orders-show',
        dashboard: '/orders-analytics',
        status: '/orders-update-status',
    },

    loan: {
        list: '/loan-request-list-paginate',
        show: '/loan-request-show/',
        update: '/loan-request-update/',
        create: '/loan-request-create',
        delete: '/loan-request-delete/',
    },
    letterRequests: {
        paginate: '/letter-request-list-paginate',
        create: '/letter-request-create',
        update: '/letter-request-update/',
        delete: '/letter-request-delete/',
    },
    extraServices: {
        list: '/service-request-list-paginate',
        show: '/service-request-show/',
        create: '/service-request-create',
        update: '/service-request-update/',
        delete: '/service-request-delete/',
    },
    employeeComplaint: {
        list: '/employee-complaint-list-paginate',
        show: '/employee-complaint-show/',
        update: '/employee-complaint-update/',
        create: '/employee-complaint-create',
        delete: '/employee-complaint-delete/',
    },
};
